import { storage, db } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Upload listing PDF to Firebase Storage
 */
export async function uploadListingPDF(
  file: File,
  userId: string
): Promise<{ fileName: string; downloadURL: string }> {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `listing-${timestamp}-${sanitizedFileName}`;
    
    // Upload to listings/{userId}/{fileName}
    const storageRef = ref(storage, `listings/${userId}/${fileName}`);
    await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return { fileName, downloadURL };
  } catch (error) {
    console.error('Error uploading listing PDF:', error);
    throw new Error('Failed to upload listing PDF');
  }
}

/**
 * Create listing document from manual field entry (no PDF)
 */
export async function createListingManually(
  userId: string,
  fields: {
    address: string;
    city?: string;
    state?: string;
    zipCode?: string;
    mlsNumber?: string;
    listingPrice?: number | null;
    originalListingPrice?: number | null;
    listingDate?: string | null;
  }
): Promise<string> {
  try {
    const listingRef = await addDoc(collection(db, 'listings'), {
      userId,
      address: fields.address,
      city: fields.city || null,
      state: fields.state || null,
      zipCode: fields.zipCode || null,
      mlsNumber: fields.mlsNumber || null,
      listingPrice: fields.listingPrice ?? null,
      originalListingPrice: fields.originalListingPrice ?? null,
      listingDate: fields.listingDate || null,
      status: 'active',
      parsingStatus: 'completed',
      parsingError: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return listingRef.id;
  } catch (error) {
    console.error('Error creating listing manually:', error);
    throw new Error('Failed to create listing');
  }
}

/**
 * Create listing document in Firestore
 * This triggers the processListingPDF Cloud Function
 */
export async function createListingFromPDF(
  userId: string,
  fileName: string,
  downloadURL: string
): Promise<string> {
  try {
    const listingRef = await addDoc(collection(db, 'listings'), {
      userId,
      sourceFileName: fileName,
      // Temporary placeholder values - will be updated by Cloud Function
      address: 'Processing...',
      city: null,
      state: null,
      zipCode: null,
      status: 'pending',
      parsingStatus: 'pending',
      parsingError: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    
    return listingRef.id;
  } catch (error) {
    console.error('Error creating listing:', error);
    throw new Error('Failed to create listing');
  }
}
